import CheckIcon from '@mui/icons-material/Check'
import { CircularProgress, styled } from '@mui/material'
import { DialogContent, colors, typography } from '@resiliatech/birdsong'
import { useEffect, useState } from 'react'
import {
  ConstructionOutlined,
  DocumentScannerOutlined,
  DrawOutlined,
} from '@mui/icons-material'
import { StoryVariants } from './StoryAiTypes'

type Props = {
  loading: boolean
  completed: boolean
  storyVariant: StoryVariants
}

const DIALOG_HEIGHT = {
  [StoryVariants.STORY]: `324px`,
  [StoryVariants.GOALS]: `346px`,
}

const VARIANT_DATA = {
  [StoryVariants.STORY]: {
    loaders: [
      {
        text: 'Reviewing your impact',
        timing: 3000,
        icon: <DocumentScannerOutlined />,
      },
      { text: 'Drafting your story', timing: 5000, icon: <DrawOutlined /> },
      {
        text: 'Assembling your story',
        timing: 0, // this wont be used - last section should show ~ 4 seconds
        icon: <ConstructionOutlined />,
      },
    ],
    disclaimer: 'This could take 10+ seconds',
  },
  [StoryVariants.GOALS]: {
    loaders: [
      {
        text: 'Reviewing your request',
        timing: 6000,
        icon: <DocumentScannerOutlined />,
      },
      {
        text: 'Gathering your goal progress',
        timing: 8000,
        icon: <ConstructionOutlined />,
      },
      { text: 'Drafting your story', timing: 6000, icon: <DrawOutlined /> },
      {
        text: 'Assembling your story',
        timing: 0, // this wont be used - last section should show ~ 5 seconds
        icon: <ConstructionOutlined />,
      },
    ],
    disclaimer: `Please hold! This may take 30+ seconds as we gather and review your data to create the best possible story`,
  },
}

export const StoryAiLoader = ({
  loading,
  completed,
  storyVariant = StoryVariants.STORY,
}: Props) => {
  const [progress, setProgress] = useState<number>(0)

  useEffect(() => {
    let timeoutId: NodeJS.Timeout
    const variant = VARIANT_DATA[storyVariant].loaders
    const maxProgress = storyVariant === StoryVariants.GOALS ? 4 : 3

    const incrementProgress = () => {
      if (progress === 0) {
        timeoutId = setTimeout(() => setProgress(1), variant[0]?.timing)
      } else if (progress === 1) {
        timeoutId = setTimeout(() => setProgress(2), variant[1].timing)
      }
      if (progress === 2 && storyVariant === StoryVariants.GOALS) {
        timeoutId = setTimeout(() => setProgress(3), variant[2]?.timing)
      }
    }

    if (loading && progress < maxProgress) {
      incrementProgress()
    }
    if (completed) {
      setProgress(0)
    }

    return () => clearTimeout(timeoutId)
  }, [loading, progress, completed, storyVariant])

  const renderLoaders = () =>
    VARIANT_DATA[storyVariant].loaders.map((step, index) => (
      <Section
        loaded={progress > index || completed}
        key={`${storyVariant}-${step.text}`}
      >
        {step.icon}
        <span style={{ flex: 1, marginLeft: '20px' }}>{step.text}</span>
        {progress > index || completed ? (
          <CheckIcon />
        ) : (
          <CircularProgress size='24px' sx={{ color: '#4D4D4D' }} />
        )}
      </Section>
    ))

  return (
    <Loader loading={loading || completed} height={DIALOG_HEIGHT[storyVariant]}>
      {renderLoaders()}

      <Note>{VARIANT_DATA[storyVariant].disclaimer}</Note>
    </Loader>
  )
}

const Loader = styled(DialogContent, {
  shouldForwardProp: (prop) => prop !== 'loading' && prop !== 'height',
})<{ loading?: boolean; height: string }>`
  display: flex;
  flex-direction: column;
  gap: 12px;
  opacity: ${({ loading }) => (loading ? 1 : 0)};
  position: absolute;
  top: 84px;
  height: ${({ loading, height }) => (loading ? height : 0)};
  width: ${({ loading }) => (loading ? '100%' : 0)};
  ${typography.up1.semiBoldSans.styles}
  color: ${colors.copy.headerDark};
  pointer-events: ${({ loading }) => (loading ? 'auto' : 'none')};
  transition: opacity 0.5s ease-in-out;
  overflow: hidden;
`
const Section = styled('div')<{ loaded: boolean }>`
  padding: 20px;
  border-radius: 8px;
  display: flex;
  height: 82px;
  align-items: center;
  justify-content: space-between;
  background: ${({ loaded }) =>
    loaded ? colors.background.primaryBackground : 'rgba(0, 0, 0, 0.06)'};
  transition: all 0.3s ease-in-out;
`

const Note = styled('div')`
  ${typography.up1.regularSans.styles};
  color: rgba(0, 0, 0, 0.87);
  text-align: center;
  font-size: 12px;
  line-height: 150%; /* 18px */
`
